import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    ClientEventHandler,
    IMessagesToClient,
    ISubscriptionPayloadParams,
} from "@finbackoffice/websocket-client";
import { WebsocketContext } from "../providers";
import { EventStatus } from "@finbackoffice/enums";

function makeStableKey(value: unknown) {
    try {
        return JSON.stringify(value);
    } catch {
        return Math.random().toString(36);
    }
}

export const useWebsocketUpdates = <T extends IMessagesToClient>(
    event: T,
    handler: ClientEventHandler<T>,
    options?: {
        subscribeByDefault?: boolean;
        statusesToDelete?: EventStatus[];
    },
    ...payload: ISubscriptionPayloadParams<T>
) => {
    const { subscribeByDefault = true, statusesToDelete } = options ?? {};
    const { connected, socketClient, debug } = useContext(WebsocketContext);

    const [isActive, setIsActive] = useState<boolean>(subscribeByDefault);

    const handlerRef = useRef(handler);

    const payloadKey = useMemo(() => makeStableKey(payload), [payload]);
    const payloadRef = useRef(payload);

    const statusesToDeleteKey = useMemo(() => makeStableKey(statusesToDelete), [statusesToDelete]);
    const statusesToDeleteRef = useRef(statusesToDelete);

    useEffect(() => {
        payloadRef.current = payload;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payloadKey]);

    useEffect(() => {
        statusesToDeleteRef.current = statusesToDelete;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusesToDeleteKey]);

    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    const stableHandler = useMemo(
        () =>
            ((...args: Parameters<ClientEventHandler<T>>) => {
                handlerRef.current?.(...args);
            }) as ClientEventHandler<T>,
        [],
    );

    useEffect(() => {
        if (!isActive || !socketClient || !connected) return;

        const payload = payloadRef.current?.[0] as unknown as { token?: string };
        const hasInValidToken =
            payload &&
            Object.hasOwn(payload, "token") &&
            typeof payload?.token === "string" &&
            payload?.token?.length === 0;

        if (hasInValidToken) {
            socketClient.off(event, stableHandler);
            return;
        }

        if (debug) console.debug("subscribe", event, payloadRef.current);
        socketClient.on(
            event,
            stableHandler,
            ...(payloadRef.current ?? []),
            ...(statusesToDeleteRef.current !== undefined ? [statusesToDeleteRef.current] : []),
        );

        return () => {
            if (debug) console.debug("unsubscribe", event);
            socketClient.off(event, stableHandler);
        };
    }, [
        isActive,
        socketClient,
        connected,
        event,
        payloadKey,
        stableHandler,
        debug,
        statusesToDeleteKey,
    ]);

    const subscribe = useCallback(() => {
        setIsActive(true);
    }, []);
    const unsubscribe = useCallback(() => {
        setIsActive(false);
    }, []);

    return {
        subscribe,
        unsubscribe,
    };
};
